import { useAuthRefresh } from "@api/auth";
import { useMessagesMetrics } from "@api/metric";
import { useThreadsStats } from "@api/thread";
import { BlockerModal } from "@components/blocker-modal";
import SidebarNavigation from "@components/sidebar-navigation";
import { useBlocker } from "@lib/blocker";
import useActiveGym from "@ui/hooks/active-gym";
import type { Breadcrumb } from "@ui/types";
import Head from "next/head";
import { useRouter } from "next/router";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { FeatureAnnouncementDialog } from "@/components/feature-announcements/announcement-dialog";
import { isTabBarPathname, MobileTabBar, TAB_BAR_HEIGHT } from "@/components/mobile-tab-bar";
import { NotificationsPrompt } from "@/components/notifications-prompt";
import { AppErrorBoundary } from "@/components/ui/error-boundary";
import { sidebarNavigationGroups, sidebarSubNavigation } from "@/lib/constants/app-navigation";
import {
  useHeliosAssistantFeature,
  useHeliosPagesFeature,
  useHeliosPulseFeature,
  useHeliosStagesFeature,
  useHeliosQrCodeFeature,
  useHeliosTasksFeature,
  useHeliosWebCallingFeature,
} from "@/lib/feature-flags/use-feature-flag";
import { HelpSupportModal } from "@/modules/help-support/help-support-modal";
import type { SecondaryNavMode } from "./sidebar-navigation";
import useActiveOrg from "../hooks/active-org";
import useActiveSubscription from "../hooks/active-subscription";

interface MainLayoutProps {
  children: ReactNode;
  title?: string;
  background?: string;
  hideSidebar?: boolean;
  breadcrumbs?: Breadcrumb[];
  /**
   * Secondary nav panel (settings / analytics) that slides in over the main
   * nav body while the logo header and org/gym switcher stay in place.
   */
  secondaryNav?: ReactNode;
  secondaryNavMode?: SecondaryNavMode;
}

function MainLayout({
  children,
  title,
  hideSidebar = false,
  secondaryNav,
  secondaryNavMode,
}: MainLayoutProps) {
  const [isTabActive, setIsTabActive] = useState(true);
  const [helpSupportOpen, setHelpSupportOpen] = useState(false);
  const { subscription } = useActiveSubscription();
  const { org } = useActiveOrg();
  const { gym } = useActiveGym();
  const { open, setOpen } = useBlocker();
  const router = useRouter();

  const { data: stats } = useThreadsStats(
    { gym: gym?.id, status: "unread" },
    { refreshInterval: 30 * 1000 },
  );
  const { data: metrics } = useMessagesMetrics({
    org: org?.id,
    interval: "all",
    startDate: subscription?.currentInvoiceDate,
    endDate: subscription?.nextInvoiceDate,
  });

  const { enabled: qrCodeEnabled } = useHeliosQrCodeFeature();
  const { enabled: assistantEnabled } = useHeliosAssistantFeature();
  const { enabled: pagesEnabled } = useHeliosPagesFeature();
  const { enabled: pulseEnabled } = useHeliosPulseFeature();
  const { enabled: stagesEnabled } = useHeliosStagesFeature();
  const { enabled: callingEnabled } = useHeliosWebCallingFeature();
  const { enabled: tasksEnabled } = useHeliosTasksFeature();

  const featureFlagEnabled = useMemo<Record<string, boolean>>(
    () => ({
      "helios-qr-codes": qrCodeEnabled,
      "helios-assistant": assistantEnabled,
      "helios-pages": pagesEnabled,
      "helios-pulse": pulseEnabled,
      "helios-stages": stagesEnabled,
      "helios-web-calling": callingEnabled,
      "helios-tasks": tasksEnabled,
    }),
    [
      qrCodeEnabled,
      assistantEnabled,
      pagesEnabled,
      pulseEnabled,
      stagesEnabled,
      callingEnabled,
      tasksEnabled,
    ],
  );

  const navigationGroups = useMemo(
    () =>
      sidebarNavigationGroups
        .map((group) => ({
          ...group,
          items: group.items
            .filter(
              (item) =>
                (!item.featureFlag || featureFlagEnabled[item.featureFlag]) &&
                (!item.hiddenWhenFeatureFlag || !featureFlagEnabled[item.hiddenWhenFeatureFlag]),
            )
            .map((item) => ({
              ...item,
              current: [item.href, ...(item.currentPrefixes ?? [])].some((prefix) =>
                router.route.startsWith(prefix),
              ),
              count: item.href === "/messages" && stats?.count ? Number(stats.count) : undefined,
              hotspot:
                item.hotspot &&
                (!item.hotspot.anyFeatureFlag ||
                  item.hotspot.anyFeatureFlag.some((flag) => featureFlagEnabled[flag])) &&
                (!item.hotspot.hiddenWhenFeatureFlag ||
                  !featureFlagEnabled[item.hotspot.hiddenWhenFeatureFlag])
                  ? item.hotspot
                  : undefined,
            })),
        }))
        .filter((group) => group.items.length > 0),
    [router.route, stats, featureFlagEnabled],
  );

  const subNavigation = useMemo(
    () =>
      sidebarSubNavigation.map((item) => ({
        ...item,
        current: router.route.startsWith(item.href),
        ...(item.id === "help-support" ? { onClick: () => setHelpSupportOpen(true) } : {}),
      })),
    [router.route, stats],
  );

  useAuthRefresh();

  const showTabBar = isTabBarPathname(router.pathname);

  // Expose the tab bar height as a CSS var on <html> so .h-content /
  // .min-h-content (and any portalled surfaces) can subtract it. Set on the
  // root element, not this component's tree, because bottom sheets and call
  // drawers portal to document.body. Immersive routes leave it unset (0).
  useEffect(() => {
    const root = document.documentElement;
    if (showTabBar) {
      root.style.setProperty("--app-tab-bar-h", TAB_BAR_HEIGHT);
    }
    return () => {
      root.style.removeProperty("--app-tab-bar-h");
    };
  }, [showTabBar]);

  useEffect(() => {
    const handleVisibilityChange = () => setIsTabActive(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // The shell is viewport-sized. Keep html/body from growing a second
  // scrollbar if a flex child still reports a min-content height above 100dvh.
  useEffect(() => {
    const root = document.documentElement;
    const { body } = document;
    root.classList.add("overflow-hidden");
    body.classList.add("overflow-hidden");
    return () => {
      root.classList.remove("overflow-hidden");
      body.classList.remove("overflow-hidden");
    };
  }, []);

  useEffect(() => {
    const baseTitle = title ? `${title} | Helios` : "Helios";
    const countPrefix = stats?.count && !isTabActive ? `(${stats.count}) ` : "";
    document.title = `${countPrefix}${baseTitle}`;
  }, [title, stats?.count, isTabActive]);

  return (
    <>
      <Head>
        <title>{document.title}</title>
        <meta name="description" content="Lead generation and CRM tool for gyms." />
      </Head>
      <NotificationsPrompt />
      <div className="bg-background flex h-dvh max-h-dvh min-h-0 overflow-hidden">
        {!hideSidebar && (
          <div className="md:flex md:flex-shrink-0 hidden h-full min-h-0 overflow-hidden">
            <div className="flex h-full min-h-0 w-64 flex-col overflow-hidden">
              <SidebarNavigation
                groups={navigationGroups}
                subItems={subNavigation}
                gym={gym}
                metrics={metrics}
                secondaryNav={secondaryNav}
                secondaryNavMode={secondaryNavMode}
              />
            </div>
          </div>
        )}
        {/* Mobile chrome is the bottom tab bar (rendered below); immersive
            routes (see IMMERSIVE_PATHNAMES) show no chrome and own the full
            viewport with their builder/wizard headers. */}
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
          {/*
           * Scope the boundary to the content column so a render error in the
           * page body shows the fallback *here* while the sidebar / mobile
           * header stay mounted and navigable. `resetKey={router.asPath}`
           * clears the fallback automatically once the user navigates away.
           * The fill wrapper is required because the class boundary does not
           * emit a DOM node, and flex-1 / min-h-0 must live on a real box.
           */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <AppErrorBoundary tone="page" scopeLabel={title} resetKey={router.asPath}>
              {children}
            </AppErrorBoundary>
          </div>
        </div>
        {showTabBar && (
          <MobileTabBar unreadCount={stats?.count ? Number(stats.count) : undefined} />
        )}
      </div>
      {open && <BlockerModal gym={gym?.id} open={open} setOpen={setOpen} />}
      <HelpSupportModal open={helpSupportOpen} setOpen={setHelpSupportOpen} />
      <FeatureAnnouncementDialog />
    </>
  );
}

export default MainLayout;
